// src/utils/imageUrl.js
/**
 * Resilient Image URL Resolver and Offline Luxury Placeholder.
 * Handles origin rewriting (staging vs localhost vs LAN IP),
 * eliminates mixed-content SSL errors, and guarantees zero broken 404 icons.
 */

// Sleek dark-obsidian vector canvas placeholder with luxury indigo/cyan accents
export const ART_PLACEHOLDER_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="0 0 600 600" fill="none">
  <defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="rgb(12, 14, 20)"/>
      <stop offset="50%" stop-color="rgb(20, 23, 34)"/>
      <stop offset="100%" stop-color="rgb(8, 9, 13)"/>
    </linearGradient>
    <linearGradient id="accent" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="rgb(99, 102, 241)"/>
      <stop offset="100%" stop-color="rgb(56, 189, 248)"/>
    </linearGradient>
    <radialGradient id="glow" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="rgb(99, 102, 241)" stop-opacity="0.18"/>
      <stop offset="100%" stop-color="rgb(99, 102, 241)" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="600" height="600" fill="url(#bg)"/>
  <circle cx="300" cy="300" r="220" fill="url(#glow)"/>
  <rect x="60" y="60" width="480" height="480" rx="20" stroke="rgba(255,255,255,0.08)" stroke-width="2" stroke-dasharray="8 6"/>
  
  <!-- Easel Canvas Frame -->
  <rect x="140" y="130" width="320" height="260" rx="14" fill="rgb(21, 25, 38)" stroke="rgba(255,255,255,0.12)" stroke-width="2"/>
  
  <!-- Abstract Landscape Monogram -->
  <path d="M170 330 L250 220 L310 290 L360 240 L430 330 Z" fill="url(#accent)" fill-opacity="0.25" stroke="url(#accent)" stroke-width="2" stroke-linejoin="round"/>
  <circle cx="370" cy="200" r="22" fill="rgb(56, 189, 248)" fill-opacity="0.3" stroke="rgb(56, 189, 248)" stroke-width="2"/>

  <!-- Palette / Easel Leg -->
  <line x1="220" y1="390" x2="190" y2="470" stroke="rgba(255,255,255,0.2)" stroke-width="3" stroke-linecap="round"/>
  <line x1="380" y1="390" x2="410" y2="470" stroke="rgba(255,255,255,0.2)" stroke-width="3" stroke-linecap="round"/>
  <line x1="300" y1="390" x2="300" y2="480" stroke="rgba(255,255,255,0.2)" stroke-width="3" stroke-linecap="round"/>

  <!-- Typography -->
  <text x="300" y="525" text-anchor="middle" fill="rgb(148, 163, 184)" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="15" font-weight="600" letter-spacing="2">ARTWORK PREVIEW</text>
  <text x="300" y="548" text-anchor="middle" fill="rgb(100, 116, 139)" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="11" letter-spacing="1">AUTHENTICATED DIGITAL VAULT</text>
</svg>
`)}`;

/**
 * Resolves any raw image URL from backend or user data into a browser-safe,
 * origin-safe URL for mobile devices, LAN development, and HTTPS staging.
 *
 * @param {string|null|undefined} rawUrl
 * @returns {string} Safe image URL or fallback SVG
 */
export function resolveImageUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string' || rawUrl.trim() === '') {
    return ART_PLACEHOLDER_SVG;
  }

  const url = rawUrl.trim();

  // 1. Data URLs, Blob URLs, and already-resolved SVG placeholders pass through directly
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  if (typeof window === 'undefined') {
    return url;
  }

  const currentHost = window.location.hostname;
  const currentOrigin = window.location.origin;

  // 2. Extract static sub-path if URL contains "/static/" or "/artwork_images/"
  const staticIndex = url.indexOf('/static/');
  if (staticIndex !== -1) {
    const staticSubPath = url.substring(staticIndex); // e.g. "/static/artwork_images/..."

    // If on staging or production domain, always serve via same-origin reverse proxy (eliminates SSL mismatch & mixed content)
    if (currentHost.includes('staging.artinv.online') || currentHost.includes('artinv.online')) {
      return `${currentOrigin}${staticSubPath}`;
    }

    // In local development:
    // If accessing via LAN IP from mobile (e.g. 192.168.1.105:3000), point to LAN IP backend port 5002
    if (currentHost !== 'localhost' && currentHost !== '127.0.0.1') {
      return `${window.location.protocol}//${currentHost}:5002${staticSubPath}`;
    }

    // Local desktop dev (CRA on 3000, Flask on 5002)
    if (window.location.port === '3000') {
      return `http://localhost:5002${staticSubPath}`;
    }

    return staticSubPath;
  }

  // 3. Stored relative path without "/static/" (e.g. "artwork_images/user123/...")
  if (url.startsWith('artwork_images/') || url.startsWith('owner_profile_pic/')) {
    const staticPath = `/static/${url}`;
    if (currentHost.includes('staging.artinv.online') || currentHost.includes('artinv.online')) {
      return `${currentOrigin}${staticPath}`;
    }
    if (currentHost !== 'localhost' && currentHost !== '127.0.0.1') {
      return `${window.location.protocol}//${currentHost}:5002${staticPath}`;
    }
    if (window.location.port === '3000') {
      return `http://localhost:5002${staticPath}`;
    }
    return staticPath;
  }

  // 4. Mismatched domain rewrite:
  // If backend returned "https://www.artinv.online/..." but we are on "www.staging.artinv.online"
  if (
    currentHost.includes('staging.artinv.online') &&
    (url.includes('www.artinv.online') || url.includes('artinv.online') || url.includes('localhost'))
  ) {
    try {
      const parsed = new URL(url);
      return `${currentOrigin}${parsed.pathname}${parsed.search}`;
    } catch {
      // Fallback below
    }
  }

  // 5. Normal full HTTP/HTTPS external image
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // If our page is HTTPS, upgrade http to https if possible to prevent mixed content
    if (window.location.protocol === 'https:' && url.startsWith('http://localhost')) {
      // Mobile cannot access localhost over HTTPS
      return ART_PLACEHOLDER_SVG;
    }
    return url;
  }

  // 6. Absolute path on current domain
  if (url.startsWith('/')) {
    return url;
  }

  return url;
}

/**
 * Standard error handler for <img> elements that gracefully swaps in the SVG placeholder
 * without infinite re-render loops.
 */
export function onImageError(e) {
  if (e && e.target) {
    e.target.onerror = null; // Prevent infinite fallback loops
    e.target.src = ART_PLACEHOLDER_SVG;
  }
}
